import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';

import Text from '../../ui/Text';
import Title from '../../ui/Title';
import Link from '../../ui/Link';
import { CardStatus, CardStatusText, IconCheck, IconContainer, IconError } from '../../ui/CardStatus';
import Spinner from '../../ui/Spinner';
import Input from '../../ui/Form/Input';
import { AccentButton } from '../../ui/Button';

import * as Styles from './FormStyles';

import { sendEmailRecoverry } from '../../../services/recoveryPassword';

export interface StateInterface {
  from: {
    pathname: string;
  };
}

type FormValues = {
  email: string;
};

const validate = (values: FormValues): Partial<FormValues> => {
  const errors: Partial<FormValues> = {};

  if (!values.email) {
    errors.email = 'O e-mail é obrigatório';
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = 'E-mail inválido';
  }

  return errors;
};

const Form: React.FC = () => {
  const history = useHistory();

  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [wasSendedEmail, setWasSendedEmail] = useState(false);

  const form = useFormik<FormValues>({
    initialValues: {
      email: '',
    },
    validate,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        setSending(true);
        setWasSendedEmail(false);
        setEmailError(false);
        setError('');

        const { email } = values;
        const data = {
          email,
          url: window.location.href.replace('forgot-password', 'reset-password'),
        };
        await sendEmailRecoverry(data);
        setWasSendedEmail(true);
      } catch (err: any) {
        setError('E-mail não encontrado');
        if (err?.response === undefined || err?.response?.data?.errors[0]?.detail !== 'Email não encontrado') {
          setWasSendedEmail(true);
          setEmailError(true);
        }
      } finally {
        setSending(false);
      }
    },
  });

  useEffect(() => {
    setError('');
  }, [form.values]);

  const showError = (inputName: keyof FormValues): boolean =>
    Boolean((form.errors[inputName] || error) && form.touched[inputName]);

  const handleTryAgain = (): void => {
    setSending(false);
    setWasSendedEmail(false);
    setEmailError(false);
    setError('');
  };

  return (
    <Styles.Container>
      {!wasSendedEmail ? (
        <>
          <Styles.TitleContainer>
            <Title align="center" color="black" fontWeight={700} fontSize={24} marginBottom={8}>
              Esqueceu sua senha?
            </Title>
            <Text align="center" color="black">
              Digite seu endereço de e-mail abaixo e nós lhe enviaremos um link para redefinir sua senha.
            </Text>
          </Styles.TitleContainer>

          <Styles.Form onSubmit={form.handleSubmit}>
            <Input
              onInputChange={form.handleChange}
              name="email"
              label="E-mail"
              errors={showError('email') ? form.errors.email || error : undefined}
              placeholder="Digite seu e-mail de cadastro"
              type="text"
              required
            />
            <AccentButton type="submit" bgColor="primary" disabled={sending} height={55}>
              {sending ? <Spinner size="18px" /> : 'Enviar link de redefinição'}
            </AccentButton>
          </Styles.Form>
          <Text align="center" color="black">
            Lembrei minha senha! <Link to="/login">Voltar ao login</Link>
          </Text>
        </>
      ) : (
        <Styles.TitleContainer>
          {!emailError ? (
            <>
              <Title align="center" color="black" fontWeight={700} fontSize={24}>
                E-mail enviado!
              </Title>
              <CardStatus>
                <IconContainer type="success">
                  <IconCheck />
                </IconContainer>

                <CardStatusText>
                  <Text color="black">
                    Verifique o e-mail ou caixa de SPAM de <strong>{form?.values?.email}</strong> para acessar o link de
                    redefinição da senha.
                  </Text>
                </CardStatusText>
              </CardStatus>
            </>
          ) : (
            <>
              <Title align="center" color="black" fontWeight={700} fontSize={24}>
                Oops!
              </Title>
              <CardStatus>
                <IconContainer type="danger">
                  <IconError />
                </IconContainer>

                <CardStatusText>
                  <Text color="black">
                    Não foi possível enviar o e-mail de redefinição de senha. Por favor, tente novamente.
                  </Text>
                  <Text color="black">Caso o problema persista, entre em contato com o nosso suporte.</Text>
                </CardStatusText>
              </CardStatus>
            </>
          )}

          <AccentButton type="button" bgColor="primary" height={55} onClick={() => history.push('/login')}>
            Voltar ao login
          </AccentButton>

          <Text align="center" color="black">
            Não recebeu o e-mail?{' '}
            <Styles.ActionButton onClick={() => handleTryAgain()}>Tente novamente.</Styles.ActionButton>
          </Text>
        </Styles.TitleContainer>
      )}
    </Styles.Container>
  );
};
export default Form;
